import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Charge, Penalty, BillableModuleItem } from '../types/models';

export interface CreateChargeData {
  service_id: number;
  zone_id: number;
  amount: number;
  status?: 'active' | 'inactive';
}

export interface CreatePenaltyData {
  service_id: number;
  billing_cycle_id?: number;
  type: string;
  frequency?: string;
  amount: number;
  status?: 'active' | 'inactive';
}

export interface CreateBillableItemData {
  type: 'schedule' | 'part' | 'category' | 'service' | 'charge';
  name: string;
  description?: string;
  code?: string;
  unit_price: number;
  unit_type?: string;
  is_active?: boolean;
  sort_order?: number;
  metadata?: Record<string, any>;
  county_id: number;
}

class BillingService {
  // Charge endpoints
  async getCharges(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<Charge>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Charge>>(`/county/modules/${moduleId}/charges${params}`);
  }

  async getCharge(moduleId: number, id: number): Promise<ApiResponse<Charge>> {
    return apiClient.get<Charge>(`/county/modules/${moduleId}/charges/${id}`);
  }

  async createCharge(moduleId: number, data: CreateChargeData): Promise<ApiResponse<Charge>> {
    return apiClient.post<Charge>(`/county/modules/${moduleId}/charges`, data);
  }

  async updateCharge(moduleId: number, id: number, data: Partial<CreateChargeData>): Promise<ApiResponse<Charge>> {
    return apiClient.put<Charge>(`/county/modules/${moduleId}/charges/${id}`, data);
  }

  async deleteCharge(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/charges/${id}`);
  }

  async updateChargeStatus(moduleId: number, id: number, status: 'active' | 'inactive'): Promise<ApiResponse<Charge>> {
    return apiClient.post<Charge>(`/county/modules/${moduleId}/charges/${id}/status`, { status });
  }

  // Penalty endpoints
  async getPenalties(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<Penalty>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Penalty>>(`/county/modules/${moduleId}/penalties${params}`);
  }

  async getPenalty(moduleId: number, id: number): Promise<ApiResponse<Penalty>> {
    return apiClient.get<Penalty>(`/county/modules/${moduleId}/penalties/${id}`);
  }

  async createPenalty(moduleId: number, data: CreatePenaltyData): Promise<ApiResponse<Penalty>> {
    return apiClient.post<Penalty>(`/county/modules/${moduleId}/penalties`, data);
  }

  async updatePenalty(moduleId: number, id: number, data: Partial<CreatePenaltyData>): Promise<ApiResponse<Penalty>> {
    return apiClient.put<Penalty>(`/county/modules/${moduleId}/penalties/${id}`, data);
  }

  async deletePenalty(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/penalties/${id}`);
  }

  async updatePenaltyStatus(moduleId: number, id: number, status: 'active' | 'inactive'): Promise<ApiResponse<Penalty>> {
    return apiClient.post<Penalty>(`/county/modules/${moduleId}/penalties/${id}/status`, { status });
  }

  // BillableModuleItem endpoints
  async getBillableItems(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<BillableModuleItem>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<BillableModuleItem>>(`/county/modules/${moduleId}/billable-items${params}`);
  }

  async getBillableItem(moduleId: number, id: number): Promise<ApiResponse<BillableModuleItem>> {
    return apiClient.get<BillableModuleItem>(`/county/modules/${moduleId}/billable-items/${id}`);
  }

  async createBillableItem(moduleId: number, data: CreateBillableItemData): Promise<ApiResponse<BillableModuleItem>> {
    return apiClient.post<BillableModuleItem>(`/county/modules/${moduleId}/billable-items`, data);
  }

  async updateBillableItem(moduleId: number, id: number, data: Partial<CreateBillableItemData>): Promise<ApiResponse<BillableModuleItem>> {
    return apiClient.put<BillableModuleItem>(`/county/modules/${moduleId}/billable-items/${id}`, data);
  }

  async deleteBillableItem(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/billable-items/${id}`);
  }

  async toggleBillableItemStatus(moduleId: number, id: number): Promise<ApiResponse<BillableModuleItem>> {
    return apiClient.post<BillableModuleItem>(`/county/modules/${moduleId}/billable-items/${id}/toggle-status`);
  }

  async reorderBillableItems(moduleId: number, items: Array<{ id: number; sort_order: number }>): Promise<ApiResponse> {
    return apiClient.post(`/county/modules/${moduleId}/billable-items/reorder`, { items });
  }

  async getBillableItemsByType(moduleId: number, type: string): Promise<ApiResponse<BillableModuleItem[]>> {
    return apiClient.get<BillableModuleItem[]>(`/county/modules/${moduleId}/billable-items/type/${type}`);
  }

  async getActiveBillableItems(moduleId: number): Promise<ApiResponse<BillableModuleItem[]>> {
    return apiClient.get<BillableModuleItem[]>(`/county/modules/${moduleId}/billable-items/active`);
  }
}

export const billingService = new BillingService();
export default billingService;
